import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { MasterDataListItem } from '../../components/MasterDataListItem';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { getKioskModuleCapabilities } from '../../config/kioskCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useKiosks } from '../../hooks/useKiosks';
import { kioskStatusLabel } from '../../services/kioskAdminService';
import { KIOSK_STATUS } from '../../types/kiosk';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'KioskList'>;

export function KioskListScreen({ navigation }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      chip: {
        borderWidth: 1,
        borderColor: palette.border,
        borderRadius: 999,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        backgroundColor: palette.surface,
      },
      chipText: { ...typography.caption, color: palette.textPrimary },
      chipSelected: {
        borderColor: palette.primary,
        backgroundColor: palette.surface,
      },
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getKioskModuleCapabilities(auth.permissions);
  const list = useKiosks();
  const [selectedStatus, setSelectedStatus] = useState<string | null>(
    KIOSK_STATUS.ACTIVE,
  );

  const applyStatus = useCallback(
    (status: string | null) => {
      setSelectedStatus(status);
      list.applyFilters(status ? { status } : {});
    },
    [list],
  );

  const footer = useMemo(() => {
    if (!list.isLoadingMore) return null;
    return (
      <View style={styles.footer}>
        <ActivityIndicator color={palette.primary} />
      </View>
    );
  }, [list.isLoadingMore, palette.primary]);

  const hasFilters = selectedStatus !== KIOSK_STATUS.ACTIVE;

  return (
    <PermissionGuard
      permission={PERMISSIONS.SETTINGS_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader
          title="Kiosks"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
          <View style={styles.filterRow}>
            {[
              { label: 'All', value: null },
              { label: 'Pending', value: KIOSK_STATUS.PENDING },
              { label: 'Active', value: KIOSK_STATUS.ACTIVE },
              { label: 'Inactive', value: KIOSK_STATUS.INACTIVE },
            ].map(option => {
              const selected = selectedStatus === option.value;
              return (
                <Pressable
                  key={option.label}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  accessibilityLabel={`Show ${option.label.toLowerCase()} kiosks`}
                  onPress={() => applyStatus(option.value)}
                  style={[themed.chip, selected && themed.chipSelected]}
                >
                  <Text style={themed.chipText}>{option.label}</Text>
                </Pressable>
              );
            })}
          </View>

          {capabilities.canRegisterKiosk ? (
            <AppButton
              title="Register kiosk"
              onPress={() => navigation.navigate(routes.kioskCreate)}
            />
          ) : null}

          {list.isLoading && list.items.length === 0 ? (
            <View style={styles.center}>
              <ActivityIndicator color={palette.primary} />
            </View>
          ) : list.error && list.items.length === 0 ? (
            <ErrorState message={list.error} onRetry={list.retry} />
          ) : (
            <FlatList
              data={list.items}
              keyExtractor={item => String(item.kioskId)}
              refreshControl={
                <RefreshControl
                  refreshing={list.isRefreshing}
                  onRefresh={list.refresh}
                />
              }
              onEndReached={list.loadMore}
              onEndReachedThreshold={0.4}
              ListFooterComponent={footer}
              ListEmptyComponent={
                <EmptyState
                  title={
                    hasFilters
                      ? 'No kiosks match this status.'
                      : 'No kiosks registered yet.'
                  }
                  message={
                    hasFilters
                      ? 'Try another status filter.'
                      : 'Register a kiosk, then generate an activation QR for the device.'
                  }
                  actionTitle={hasFilters ? 'Clear filter' : 'Retry'}
                  onAction={
                    hasFilters ? () => applyStatus(null) : list.retry
                  }
                />
              }
              contentContainerStyle={styles.listContent}
              renderItem={({ item }) => (
                <MasterDataListItem
                  title={item.kioskName}
                  code={`#${item.kioskId}`}
                  subtitle={
                    item.deviceIdentifier
                      ? `Device: ${item.deviceIdentifier}`
                      : item.deviceName || 'Not activated yet'
                  }
                  status={kioskStatusLabel(item.status)}
                  onPress={() =>
                    navigation.navigate(routes.kioskDetails, {
                      kioskId: item.kioskId,
                    })
                  }
                />
              )}
            />
          )}

        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg, gap: spacing.sm },
  filterRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl, gap: spacing.sm },
  footer: { paddingVertical: spacing.lg, alignItems: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
